import React from 'react';
import { observer } from 'mobx-react';
import { Trans, withNamespaces } from 'react-i18next';
import PropTypes from 'prop-types';

import { Date } from 'apps/components/date';
import { FullWidthDivider } from 'legacyComponents/styled/lib';
import { Heading, Paragraph } from 'components';

import {
  CheckoutItem,
  PriceDetailsWrapper,
  PriceDetails,
  AddPaymentMethodLink as SeatsLink,
  SmallSeatInput,
} from 'apps/payment/styles';

@withNamespaces()
@observer
export class NewMonthlyTotal extends React.Component {
  onChangeAddedSeats = e => {
    const { paymentSpacePreview } = this.props;
    const { preview } = paymentSpacePreview;
    if (!preview) return;
    let addedSeats = e.target.value;
    const max = parseInt(e.target.max, 10);
    const min = parseInt(e.target.min, 10);
    const addedN = parseInt(addedSeats, 10);
    if (addedN < min) {
      addedSeats = '';
    }
    if (addedN > max) {
      addedSeats = max;
    }
    e.target.value = addedSeats;
    paymentSpacePreview.UpdateExtraSeats(addedSeats);
    preview.seats.Update('add', addedSeats, { refresh: true });
  };

  toggleAddingSeats = () => {
    const { paymentSpacePreview } = this.props;
    const { preview } = paymentSpacePreview;
    if (!(preview && preview.seats)) return;
    paymentSpacePreview.SetAddingExtraSeats(
      !paymentSpacePreview.addingExtraSeats,
    );
    if (!paymentSpacePreview.addingExtraSeats) {
      paymentSpacePreview.UpdateExtraSeats(0);
      preview.seats.Update('add', 0, { refresh: true });
    }
  };

  render() {
    const { paymentSpacePreview, allowAddSeats = false } = this.props;

    if (!paymentSpacePreview && paymentSpacePreview.preview) return null;

    const {
      preview,
      extraSeats,
      addingExtraSeats,
      spaceType,
    } = paymentSpacePreview;

    const {
      seats,
      billingStarts,
      displayPerSeatAmount,
      displayInitialAmount,
      displayFinalAmount,
      displayUsedExtraAmount,
      displayOptionalExtraAmount,
    } = preview;

    const { includedPlanSeats, usedExtraSeats, optionalExtraSeats } = seats;

    let extraSeatsInfo;
    let optionalSeats;

    if (usedExtraSeats) {
      extraSeatsInfo = (
        <>
          <Paragraph>
            <Trans>
              Required extra seats ({{ displayPerSeatAmount }} each)
            </Trans>{' '}
            <span>
              {displayUsedExtraAmount}/<Trans>month</Trans>
            </span>
          </Paragraph>
          <ul>
            <li>
              ✓{' '}
              <Trans>
                {{ usedExtraSeats }} seats for private collaborators
              </Trans>
            </li>
          </ul>
        </>
      );
    }

    if (addingExtraSeats || optionalExtraSeats) {
      optionalSeats = (
        <>
          <Paragraph>
            <Trans>
              Optional extra seats ({{ displayPerSeatAmount }} each)
            </Trans>
            <span>
              {displayOptionalExtraAmount}/<Trans>month</Trans>
            </span>
          </Paragraph>
          <ul>
            <li>
              ✓{' '}
              {allowAddSeats ? (
                <SmallSeatInput
                  onChange={this.onChangeAddedSeats}
                  type='number'
                  min='0'
                  value={extraSeats}
                  autoFocus
                />
              ) : (
                optionalExtraSeats
              )}{' '}
              seats for private collaborators
            </li>
          </ul>
        </>
      );
    }

    const details = (
      <PriceDetailsWrapper>
        <PriceDetails>
          <Paragraph>
            <Trans>
              {spaceType === 'profile' ? 'Individual' : 'Team'} Subscription{' '}
              <span>{displayInitialAmount}/month</span>
            </Trans>
          </Paragraph>
          <ul>
            <li>
              <strong>
                <Trans>CAD Rooms</Trans>
              </strong>
            </li>
            <li>
              ✓ <Trans>unlimited shared files</Trans>
            </li>
            <li>
              ✓ <Trans>unlimited collaborators</Trans>
            </li>
            <li style={{ marginTop: '10px' }}>
              <strong>
                <Trans>Workspace</Trans>
              </strong>
            </li>
            <li>
              ✓ <Trans>unlimited private projects</Trans>
            </li>
            <li>
              ✓{' '}
              <Trans>
                {{ includedPlanSeats }} seats for private collaborators
              </Trans>
            </li>
          </ul>
          {extraSeatsInfo}
          {optionalSeats}
        </PriceDetails>
      </PriceDetailsWrapper>
    );

    const addSeatsSection = allowAddSeats && (
      <div>
        <SeatsLink danger={!!addingExtraSeats} onClick={this.toggleAddingSeats}>
          {addingExtraSeats ? (
            <Trans>Don&apos;t add seats</Trans>
          ) : (
            <>
              + <Trans>Add seats</Trans>
            </>
          )}
        </SeatsLink>
      </div>
    );

    return (
      <CheckoutItem>
        {details}
        {addSeatsSection}
        <FullWidthDivider margin='20px 0 20px 0' />
        <Heading level={4}>
          <Trans>
            New monthly total <span>{{ displayFinalAmount }}/month</span>
          </Trans>
        </Heading>
        <Paragraph>
          <Trans>
            Starting <Date value={billingStarts} noRelative />
          </Trans>
        </Paragraph>
      </CheckoutItem>
    );
  }
}

NewMonthlyTotal.propTypes = {
  spaceType: PropTypes.string,
  allowAddSeats: PropTypes.bool,
  seatsUsage: PropTypes.object,
  paymentSpacePreview: PropTypes.object,
  startingDate: PropTypes.string,
};
